// ModeToggle — top-bar Practice ↔ Platform switch (Phase A3).
//
// Rendered ONLY for platform users (gated in AppShell). Toggling invalidates
// every react-query cache so pages refetch with the new mode-aware query
// params. A full page reload was the v1 approach but burned 4+ bootstrap
// requests on every flip (/auth/me, /notifications, /users, /current-user, …)
// and was the main culprit for hitting the global 200/15min rate limit during
// dev testing. invalidateQueries() achieves the same data refresh with
// dramatically less traffic — and feels snappier.
//
// In Practice mode the PracticeSwitcher is shown next to this control; in
// Platform mode it's hidden.

import React from 'react';
import { Building2, Globe2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store';

export default function ModeToggle() {
  const platformMode = useAuthStore((s) => s.platformMode);
  const setPlatformMode = useAuthStore((s) => s.setPlatformMode);
  const queryClient = useQueryClient();

  const handleToggle = (next: boolean) => {
    if (next === platformMode) return;
    setPlatformMode(next);
    // Soft refresh — invalidate everything; components that depend on
    // platformMode (Tasks list, AdminPage sidebar, etc.) re-read the store
    // synchronously and their queries refetch in the background.
    queryClient.invalidateQueries();
  };

  return (
    <div
      role="tablist"
      aria-label="Practice or Platform view"
      className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
    >
      <button
        role="tab"
        aria-selected={!platformMode}
        onClick={() => handleToggle(false)}
        className={`px-3 py-1 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors ${
          !platformMode
            ? 'bg-white text-[#C94B1F] shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <Building2 className="w-3.5 h-3.5" /> Practice
      </button>
      <button
        role="tab"
        aria-selected={platformMode}
        onClick={() => handleToggle(true)}
        className={`px-3 py-1 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors ${
          platformMode
            ? 'bg-white text-[#C94B1F] shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <Globe2 className="w-3.5 h-3.5" /> Platform
      </button>
    </div>
  );
}
